import { Button, Divider, Flex, Stack, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import CustomText from "components/ui/CustomText/CustomText";
import { ProductResponseType } from "constants/types/product";
import { FieldArray, FormikProps } from "formik";
import { memo, useCallback, useMemo, useRef } from "react";
import { POCreateFormValues, POItemFormValues, emptyItem } from "../../config";
import { calcOrderTotal, formatCurrency } from "../../utils";
import PurchaseOrderItemRow from "../PurchaseOrderItemRow/PurchaseOrderItemRow";

interface PurchaseOrderItemsFieldArrayProps {
  formik: FormikProps<POCreateFormValues>;
  products: ProductResponseType[];
}

type POItemField = keyof POItemFormValues;

const PurchaseOrderItemsFieldArray = memo(
  ({ formik, products }: PurchaseOrderItemsFieldArrayProps) => {
    const items = formik.values.items;

    const formikRef = useRef(formik);
    formikRef.current = formik;

    const productMap = useMemo(
      () =>
        products.reduce<Record<string, ProductResponseType>>((acc, p) => {
          acc[p.id] = p;
          return acc;
        }, {}),
      [products],
    );
    const productMapRef = useRef(productMap);
    productMapRef.current = productMap;

    const baseProductOptions = useMemo(
      () => products.map((p) => ({ label: p.name, value: p.id })),
      [products],
    );

    // 1 sản phẩm được phép nằm trên nhiều dòng của cùng 1 đơn (khác chương trình
    // hoặc mức giá), nên mọi dòng dùng chung danh sách và không disable option nào.
    // Backend cộng dồn tồn kho tuần tự trong transaction nên trùng dòng vẫn đúng.

    const itemsErrors = Array.isArray(formik.errors.items)
      ? (formik.errors.items as Partial<Record<POItemField, string>>[])
      : undefined;
    const itemsTouched = Array.isArray(formik.touched.items)
      ? (formik.touched.items as Partial<Record<POItemField, boolean>>[])
      : undefined;
    const itemsError =
      typeof formik.errors.items === "string" ? formik.errors.items : null;

    const orderTotal = useMemo(() => calcOrderTotal(items), [items]);

    const handleProductChange = useCallback(
      (index: number, productId: string | null) => {
        const currentItem = formikRef.current.values.items[index];
        const product = productId ? productMapRef.current[productId] : null;
        const nextItem: POItemFormValues = {
          ...currentItem,
          productId: productId ?? "",
          productName: product?.name ?? "",
          productUnit: product?.unit?.name ?? "",
        };
        formikRef.current.setFieldValue(`items[${index}]`, nextItem, false);
      },
      [],
    );

    const handleFieldChange = useCallback(
      (
        index: number,
        field: "quantity" | "unitPrice" | "productName",
        value: number | string,
      ) => {
        formikRef.current.setFieldValue(`items[${index}].${field}`, value, false);
      },
      [],
    );

    const handleFieldBlur = useCallback((index: number, field: POItemField) => {
      formikRef.current.setFieldTouched(`items[${index}].${field}`, true);
    }, []);

    return (
      <Stack gap="xs">
        <CustomText variant="section-title">Danh sách sản phẩm</CustomText>

        <FieldArray name="items">
          {({ push, remove }) => (
            <Stack gap="md">
              {items.map((item, index) => (
                <PurchaseOrderItemRow
                  key={item.rowKey}
                  index={index}
                  item={item}
                  itemError={itemsErrors?.[index]}
                  itemTouched={itemsTouched?.[index]}
                  productOptions={baseProductOptions}
                  onProductChange={handleProductChange}
                  onFieldChange={handleFieldChange}
                  onFieldBlur={handleFieldBlur}
                  onRemove={remove}
                  canRemove={items.length > 1}
                />
              ))}

              {itemsError && (
                <Text size="xs" c="red">
                  {itemsError}
                </Text>
              )}

              <Button
                variant="light"
                leftSection={<IconPlus size={14} />}
                onClick={() => push(emptyItem())}
                w="fit-content"
              >
                Thêm sản phẩm
              </Button>
            </Stack>
          )}
        </FieldArray>

        <Divider />

        <Flex justify="space-between" align="center" px={4}>
          <Text fw={600} size="sm">Tổng tiền đơn hàng</Text>
          <Text fw={700} size="md">{formatCurrency(orderTotal)}</Text>
        </Flex>
      </Stack>
    );
  },
  (prev, next) =>
    prev.products === next.products &&
    prev.formik.values.items === next.formik.values.items &&
    prev.formik.errors.items === next.formik.errors.items &&
    prev.formik.touched.items === next.formik.touched.items,
);

PurchaseOrderItemsFieldArray.displayName = "PurchaseOrderItemsFieldArray";

export default PurchaseOrderItemsFieldArray;
